<template>
  <Teleport :to="calStore.overlayTarget">
    <!-- Backdrop -->
    <Transition name="fade">
      <div
        v-if="calStore.settingsPanelOpen"
        class="fixed inset-0 bg-black/30 z-30"
        @click="close"
      />
    </Transition>

    <!-- Panel -->
    <Transition name="slide-over">
      <div
        v-if="calStore.settingsPanelOpen"
        class="fixed inset-y-0 right-0 w-full sm:w-[710px] bg-white dark:bg-[#1C2128] shadow-2xl z-40 flex flex-col"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-5 h-14 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
          <h2 class="text-base font-semibold text-gray-900 dark:text-white">Calendar Settings</h2>
          <button
            @click="close"
            class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Loading skeleton — mirrors left-nav + right-content split -->
        <div v-if="loadingSettings" class="flex flex-1 overflow-hidden animate-pulse">
          <!-- Left nav skeleton -->
          <div class="w-44 flex-shrink-0 border-r border-gray-100 dark:border-gray-700 pt-3 space-y-0.5 px-3 bg-gray-50/60 dark:bg-[#161b22]/50">
            <div v-for="(w, i) in ['52px', '64px', '52px']" :key="i" class="flex items-center gap-2.5 px-1 py-2.5">
              <div class="w-4 h-4 flex-shrink-0 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="h-3 rounded bg-gray-200 dark:bg-gray-700" :style="{ width: w }"></div>
            </div>
          </div>

          <!-- Right content skeleton -->
          <div class="flex-1 px-6 py-5 space-y-7">
            <!-- Section 1: Time and Date -->
            <div class="space-y-3">
              <div class="h-2.5 w-28 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <div class="h-2.5 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="h-9 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
                </div>
                <div class="space-y-1.5">
                  <div class="h-2.5 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="h-9 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
                </div>
              </div>
            </div>
            <!-- Section 2: Default View -->
            <div class="space-y-3">
              <div class="h-2.5 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <div class="h-2.5 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="h-9 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
                </div>
                <div class="space-y-1.5">
                  <div class="h-2.5 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="h-9 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
                </div>
              </div>
            </div>
            <!-- Section 3: a toggle row group -->
            <div class="space-y-3">
              <div class="h-2.5 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="space-y-3">
                <div v-for="j in 3" :key="j" class="flex items-center justify-between py-1">
                  <div class="space-y-1.5 flex-1 mr-8">
                    <div class="h-3 rounded bg-gray-200 dark:bg-gray-700" :style="{ width: j === 1 ? '120px' : j === 2 ? '96px' : '140px' }"></div>
                    <div class="h-2.5 w-48 rounded bg-gray-100 dark:bg-gray-800"></div>
                  </div>
                  <div class="w-9 h-5 rounded-full flex-shrink-0 bg-gray-200 dark:bg-gray-700"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Error -->
        <div v-else-if="settingsStore.error" class="flex-1 flex flex-col items-center justify-center px-4 text-center">
          <p class="text-sm text-red-500 mb-3">{{ settingsStore.error }}</p>
          <button @click="settingsStore.fetchSettings()" class="text-sm text-blue-600 hover:underline">Try again</button>
        </div>

        <!-- Body: left nav + right content -->
        <div v-else class="flex flex-1 overflow-hidden">

          <!-- Left tab nav -->
          <nav class="w-44 flex-shrink-0 border-r border-gray-100 dark:border-gray-700 pt-2 overflow-y-auto bg-gray-50/60 dark:bg-[#161b22]/50">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-all"
              :class="activeTab === tab.id
                ? 'text-blue-600 dark:text-blue-400 bg-white dark:bg-[#1C2128] font-medium border-r-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-white/70 dark:hover:bg-[#1C2128]/60 font-normal'"
            >
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" :d="tab.iconPath" />
              </svg>
              {{ tab.label }}
            </button>
          </nav>

          <!-- Right content area -->
          <div class="flex-1 overflow-y-auto px-6 py-5 space-y-7">

            <!-- Save error banner -->
            <div
              v-if="saveError"
              class="px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400"
            >
              {{ saveError }}
            </div>

            <!-- ── General ───────────────────────────────────── -->
            <template v-if="activeTab === 'general'">
              <section>
                <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Time and Date</p>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Time format</label>
                    <select
                      v-model="userDraft.time_format"
                      class="w-full h-9 px-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option v-for="opt in timeFormatOptions" :key="opt.key" :value="opt.key">{{ opt.value }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Date format</label>
                    <select
                      v-model="userDraft.date_format"
                      class="w-full h-9 px-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option v-for="opt in dateFormatOptions" :key="opt.key" :value="opt.key">{{ opt.value }}</option>
                    </select>
                  </div>
                </div>
              </section>

              <section>
                <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Default View</p>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Opening view</label>
                    <select
                      v-model="calDraft.default_view"
                      class="w-full h-9 px-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option v-for="v in viewOptions" :key="v.value" :value="v.value">{{ v.label }}</option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Start of week</label>
                    <select
                      v-model="userDraft.start_day_of_week"
                      class="w-full h-9 px-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option v-for="opt in weekDayOptions" :key="opt.key" :value="opt.key">{{ opt.value }}</option>
                    </select>
                  </div>
                </div>
              </section>
            </template>

            <!-- ── Timezone ──────────────────────────────────── -->
            <template v-else-if="activeTab === 'timezone'">
              <section>
                <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Primary Timezone</p>
                <div class="space-y-3">
                  <div>
                    <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Timezone</label>
                    <TimelydoDropdown
                      title=""
                      name="timezone"
                      :selected_value="userDraft.timezone"
                      :values="timezoneOptions"
                      :opts="{ search_able: true, placeholder: 'Select timezone', maximum_height: '220px', sort_able: false }"
                      @change="userDraft.timezone = $event.timezone"
                    />
                  </div>
                  <div>
                    <label class="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                      Display label
                      <span class="text-gray-400 dark:text-gray-600 font-normal">optional</span>
                    </label>
                    <input
                      v-model="userDraft.label_for_primary_timezone"
                      type="text"
                      placeholder="e.g. Home"
                      class="w-full h-9 px-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                    />
                  </div>
                </div>
              </section>

              <section>
                <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Secondary Timezone</p>
                <div class="space-y-3">
                  <CalendarSettingsToggle
                    label="Enable secondary timezone"
                    description="Show a second timezone column alongside your primary one."
                    v-model="calDraft.display_secondary_time_zone"
                  />
                  <template v-if="calDraft.display_secondary_time_zone">
                    <div>
                      <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Timezone</label>
                      <TimelydoDropdown
                        title=""
                        name="secondary_timezone"
                        :selected_value="calDraft.secondary_timezone"
                        :values="timezoneOptions"
                        :opts="{ search_able: true, placeholder: 'Select timezone', maximum_height: '220px', sort_able: false }"
                        @change="calDraft.secondary_timezone = $event.secondary_timezone"
                      />
                    </div>
                    <div>
                      <label class="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                        Display label
                        <span class="text-gray-400 dark:text-gray-600 font-normal">optional</span>
                      </label>
                      <input
                        v-model="calDraft.label_for_secondary_timezone"
                        type="text"
                        placeholder="e.g. New York"
                        class="w-full h-9 px-3 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                      />
                    </div>
                  </template>
                </div>
              </section>
            </template>

            <!-- ── Display ───────────────────────────────────── -->
            <template v-else-if="activeTab === 'display'">
              <section>
                <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Week View</p>
                <div class="divide-y divide-gray-50 dark:divide-gray-800/60">
                  <CalendarSettingsToggle
                    label="Show weekends"
                    description="Display Saturday and Sunday in week and day views."
                    :model-value="!calDraft.show_only_work_week"
                    @update:model-value="calDraft.show_only_work_week = !$event"
                  />
                  <CalendarSettingsToggle
                    label="Narrow weekend columns"
                    description="Compress weekend columns to give more space to weekdays."
                    v-model="calDraft.narrow_weekend"
                  />
                </div>
              </section>

              <section>
                <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Events</p>
                <div class="divide-y divide-gray-50 dark:divide-gray-800/60">
                  <CalendarSettingsToggle
                    label="Show current time indicator"
                    description="Show a red line at the current time on day and week views."
                    v-model="calDraft.show_now_indicator"
                  />
                  <CalendarSettingsToggle
                    label="Dim past bookings"
                    description="Reduce brightness of bookings that have already passed."
                    v-model="calDraft.reduce_brightness_of_past_bookings"
                  />
                  <CalendarSettingsToggle
                    label="Show past bookings"
                    description="Include completed bookings in the calendar view."
                    v-model="calDraft.show_past_bookings"
                  />
                  <CalendarSettingsToggle
                    label="Show declined bookings"
                    description="Display bookings you have declined."
                    v-model="calDraft.show_declined_bookings"
                  />
                  <CalendarSettingsToggle
                    label="Collapse duplicate events"
                    description="Merge repeated identical events into a single entry."
                    v-model="calDraft.collapse_duplicate_events"
                  />
                </div>
              </section>
            </template>

          </div>
        </div>

        <!-- Footer -->
        <div
          v-if="!loadingSettings && !settingsStore.error"
          class="flex items-center justify-end gap-3 px-5 py-3.5 border-t border-gray-100 dark:border-gray-700 flex-shrink-0 bg-gray-50/60 dark:bg-[#161b22]/40"
        >
          <button
            @click="close"
            class="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            @click="save"
            :disabled="saving"
            class="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-1.5"
          >
            <svg v-if="saving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {{ saving ? 'Saving...' : 'Save changes' }}
          </button>
        </div>

      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { useCalendarStore }         from 'stores/calendar.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'
import { useGlobalStore }           from 'stores/global.js'
import { useRequest }               from 'composables/useRequest.js'
import CalendarSettingsToggle  from './CalendarSettingsToggle.vue'
import TimelydoDropdown        from 'controllers/vue_components/fields/timelydo_dropdown'

const calStore      = useCalendarStore()
const settingsStore = useCalendarSettingsStore()
const globalStore   = useGlobalStore()
const { axiosRequest } = useRequest()

const activeTab   = ref('general')
const saveError   = ref(null)
const saving      = ref(false)
const loadingSettings = computed(() => (settingsStore.loading && !settingsStore.fetchedAt) || !optionsLoaded.value)

// ── Tab nav ───────────────────────────────────────────────────────────────────

const tabs = [
  {
    id:       'general',
    label:    'General',
    iconPath: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  {
    id:       'timezone',
    label:    'Timezone',
    iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    id:       'display',
    label:    'Display',
    iconPath: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
  },
]

// ── Dropdown options (fetched from init_data once) ────────────────────────────

const optionsLoaded    = ref(false)
const timeFormatOptions = ref([])
const dateFormatOptions = ref([])
const timezoneOptions   = ref([])
const weekDayOptions    = ref([])

async function loadOptions() {
  if (optionsLoaded.value) return
  const res = await axiosRequest('GET', '/api/v1/host/settings/init_data', {}, false)
  if (!res.js_error && res.response?.success) {
    const d = res.response.data
    timeFormatOptions.value = d.time_formats_list  ?? []
    dateFormatOptions.value = d.date_formats        ?? []
    timezoneOptions.value   = d.time_zone_list      ?? []
    weekDayOptions.value    = d.week_days_list       ?? []
    optionsLoaded.value = true
  }
}

// ── Draft state ───────────────────────────────────────────────────────────────

// calDraft: fields that save to PUT /api/v1/calendar/settings
function buildCalDraft() {
  return {
    display_secondary_time_zone:        settingsStore.display_secondary_time_zone        ?? false,
    secondary_timezone:                 settingsStore.secondary_timezone                 ?? '',
    label_for_secondary_timezone:       settingsStore.label_for_secondary_timezone       ?? '',
    default_view:                       settingsStore.default_view                       ?? 'week',
    show_only_work_week:                settingsStore.show_only_work_week                ?? false,
    show_now_indicator:                 settingsStore.show_now_indicator                 ?? true,
    reduce_brightness_of_past_bookings: settingsStore.reduce_brightness_of_past_bookings ?? false,
    show_past_bookings:                 settingsStore.show_past_bookings                 ?? true,
    show_declined_bookings:             settingsStore.show_declined_bookings             ?? false,
    collapse_duplicate_events:          settingsStore.collapse_duplicate_events          ?? false,
    narrow_weekend:                     settingsStore.narrow_weekend                     ?? false,
  }
}

// userDraft: fields that save to PUT /api/v1/user
function buildUserDraft() {
  const u = globalStore.user ?? {}
  return {
    time_format:              u.time_format              ?? '12_hours',
    date_format:              u.date_format              ?? '31/12/2024',
    start_day_of_week:        u.start_day_of_week        ?? 0,
    timezone:                 u.timezone                 ?? 'UTC',
    label_for_primary_timezone: u.label_for_primary_timezone ?? '',
  }
}

const calDraft  = ref(buildCalDraft())
const userDraft = ref(buildUserDraft())

watch(() => calStore.settingsPanelOpen, async (open) => {
  if (!open) return
  saveError.value = null
  await Promise.all([
    settingsStore.fetchIfStale(),
    globalStore.refreshUser(),
    loadOptions(),
  ])
  calDraft.value  = buildCalDraft()
  userDraft.value = buildUserDraft()
})

// ── Actions ───────────────────────────────────────────────────────────────────

async function save() {
  saveError.value = null
  saving.value    = true
  try {
    // Save user fields
    const userRes = await axiosRequest('PUT', '/api/v1/user', { user: userDraft.value }, false)
    if (!userRes.js_error && userRes.response?.success) {
      Object.assign(globalStore.user, userRes.response.data)
      // Patch fields not included in the current_user blueprint view
      Object.assign(globalStore.user, {
        date_format:                userDraft.value.date_format,
        start_day_of_week:          userDraft.value.start_day_of_week,
        label_for_primary_timezone: userDraft.value.label_for_primary_timezone,
      })
      globalStore.persistUserPrefs()
    } else {
      saveError.value = userRes.response?.message || 'Failed to save user settings.'
      return
    }

    // Save calendar settings
    await settingsStore.saveSettings(calDraft.value)
    if (settingsStore.error) {
      saveError.value = settingsStore.error
      return
    }

    // Apply the new default view immediately
    if (calDraft.value.default_view) calStore.setView(calDraft.value.default_view)

    calStore.closeSettingsPanel()
  } catch {
    saveError.value = 'Something went wrong.'
  } finally {
    saving.value = false
  }
}

function close() {
  calStore.closeSettingsPanel()
}

function onKeydown(e) {
  if (e.key === 'Escape' && calStore.settingsPanelOpen) close()
}

onMounted(()  => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))

// ── Static options ────────────────────────────────────────────────────────────

const viewOptions = [
  { value: 'day',   label: 'Day'    },
  { value: 'week',  label: 'Week'   },
  { value: 'month', label: 'Month'  },
  { value: 'year',  label: 'Year'   },
  { value: '4days', label: '4 Days' },
]
</script>

<style scoped>
.slide-over-enter-active { transition: transform 0.25s ease; }
.slide-over-leave-active { transition: transform 0.20s ease; }
.slide-over-enter-from,
.slide-over-leave-to    { transform: translateX(100%); }

.fade-enter-active { transition: opacity 0.2s ease; }
.fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from,
.fade-leave-to     { opacity: 0; }
</style>
